/* ============================================================
   Lucy Mini Studio — In-Chat Virtual Try-On Canvas
   Part of Lucy AI Sales Funnel
   Safe DOM construction (no innerHTML)
   ============================================================ */
(function() {
  'use strict';

  // Instance guard — Shopify re-executes scripts on section re-render
  if (window._lucyStudioInstance) return;
  window._lucyStudioInstance = true;

  // ============================================================
  // CONFIG
  // ============================================================
  var CFG = {
    CANVAS_W: 300,
    CANVAS_H: 400,
    MIN_SCALE: 0.3,
    MAX_SCALE: 2.0,
    API_BASE: 'https://wardrobe.wowstore.live',
    // MM v2 Task 2.4: tap-to-swap (Magic Mirror) backend.
    AI_API_BASE: 'https://ai-api.wowstore.live',
    LAYER_ORDER: {
      underwear: 1, base_layer: 2, bottoms: 3, tops: 4,
      dresses: 4, outerwear: 5, footwear: 6, accessories: 7, headwear: 8
    },
    BODY_POSITIONS: {
      headwear:    { x: 150, y: 30 },
      accessories: { x: 150, y: 60 },
      tops:        { x: 150, y: 140 },
      dresses:     { x: 150, y: 180 },
      base_layer:  { x: 150, y: 140 },
      outerwear:   { x: 150, y: 130 },
      bottoms:     { x: 150, y: 260 },
      footwear:    { x: 150, y: 360 },
      underwear:   { x: 150, y: 200 }
    }
  };

  // ============================================================
  // STATE
  // ============================================================
  var state = {
    items: [],        // products added to studio
    canvasItems: [],  // items placed on canvas
    selectedItem: null,
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    images: {},       // image cache
    isOpen: false,
    upsellShown: false,
    // MM v2 Task 2.4: tap-to-swap state.
    lookImageUrl: null,   // hosted URL of the currently displayed generated look
    lookId: null,         // stable id for this look session
    swapPiece: null,      // canvas piece whose alternates picker is open
    swapBusy: false       // true while a look-edit generation is in flight
  };

  // DOM references (set in buildDOM)
  var dom = {};

  // ============================================================
  // HELPERS
  // ============================================================
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  function btn(cls, text, onclick) {
    var b = el('button', cls, text);
    b.type = 'button';
    if (onclick) b.addEventListener('click', onclick);
    return b;
  }

  function guessCategory(title) {
    var t = (title || '').toLowerCase();
    if (/hat|cap|beanie|headband|turban/i.test(t)) return 'headwear';
    if (/boot|shoe|sneaker|heel|sandal|slipper|loafer/i.test(t)) return 'footwear';
    if (/dress|gown|jumpsuit|romper/i.test(t)) return 'dresses';
    if (/pant|trouser|jean|short|skirt|legging/i.test(t)) return 'bottoms';
    if (/jacket|coat|blazer|cardigan|hoodie|sweater|poncho/i.test(t)) return 'outerwear';
    if (/ring|necklace|bracelet|earring|watch|bag|belt|scarf|sunglasses/i.test(t)) return 'accessories';
    if (/bra|underwear|brief|boxer|lingerie/i.test(t)) return 'underwear';
    return 'tops';
  }

  function formatPrice(p) {
    var n = parseFloat(p);
    return isNaN(n) ? p : '\u20AC' + n.toFixed(2);
  }

  function showToast(msg) {
    var t = el('div', 'lucy-studio-toast', msg);
    document.body.appendChild(t);
    setTimeout(function() { t.classList.add('show'); }, 10);
    setTimeout(function() {
      t.classList.remove('show');
      setTimeout(function() { t.remove(); }, 300);
    }, 2500);
  }

  // ============================================================
  // BUILD DOM (safe — createElement only)
  // ============================================================
  function buildDOM() {
    var panel = el('div', 'lucy-studio-panel');

    // --- Header ---
    var header = el('div', 'studio-header');
    var title = el('h4', '', '');
    var titleIcon = document.createTextNode('\uD83D\uDC57 '); // dress emoji
    title.appendChild(titleIcon);
    title.appendChild(document.createTextNode("Lucy's Studio"));
    header.appendChild(title);
    var closeBtn = btn('studio-close-btn', '\u2715', closeStudio);
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // --- Canvas Wrap ---
    var canvasWrap = el('div', 'studio-canvas-wrap');
    var canvas = document.createElement('canvas');
    canvas.width = CFG.CANVAS_W;
    canvas.height = CFG.CANVAS_H;
    canvasWrap.appendChild(canvas);

    // Empty state
    var emptyState = el('div', 'studio-empty-state');
    var emptyEmoji = el('span', 'emoji', '\uD83D\uDC57');
    emptyState.appendChild(emptyEmoji);
    emptyState.appendChild(el('span', '', 'Tap a product to try it on'));
    canvasWrap.appendChild(emptyState);

    // Drop overlay
    var dropOverlay = el('div', 'studio-drop-overlay', 'Drop item here');
    canvasWrap.appendChild(dropOverlay);

    // MM v2 Task 2.4: generated look image overlay (hidden until a look-edit returns).
    var lookImageWrap = el('div', 'studio-look-image-wrap');
    var lookImage = document.createElement('img');
    lookImage.className = 'studio-look-image';
    lookImage.alt = 'Your look';
    lookImageWrap.appendChild(lookImage);
    var lookSpinner = el('div', 'studio-look-spinner');
    var lookSpinnerMsg = el('div', 'studio-look-spinner-msg', 'Updating your look\u2026');
    lookSpinner.appendChild(el('div', 'studio-look-spinner-ring'));
    lookSpinner.appendChild(lookSpinnerMsg);
    lookImageWrap.appendChild(lookSpinner);
    canvasWrap.appendChild(lookImageWrap);

    panel.appendChild(canvasWrap);

    // --- Items Bar ---
    var itemsBar = el('div', 'studio-items-bar');
    panel.appendChild(itemsBar);

    // --- Controls ---
    var controls = el('div', 'studio-controls');
    var layerUpBtn = btn('studio-ctrl-btn', '\u2B06 Up', function() { moveLayer(1); });
    var layerDnBtn = btn('studio-ctrl-btn', '\u2B07 Down', function() { moveLayer(-1); });
    var removeBtn = btn('studio-ctrl-btn', '\uD83D\uDDD1 Remove', removeSelected);
    var clearBtn = btn('studio-ctrl-btn', '\uD83D\uDDD1 Clear', clearCanvas);

    var scaleLabel = el('span', '', ' Scale:');
    scaleLabel.style.fontSize = '11px';
    scaleLabel.style.color = '#666';
    var scaleSlider = document.createElement('input');
    scaleSlider.type = 'range';
    scaleSlider.className = 'studio-scale-slider';
    scaleSlider.min = '30';
    scaleSlider.max = '200';
    scaleSlider.value = '100';
    scaleSlider.addEventListener('input', function() {
      if (state.selectedItem) {
        state.selectedItem.scale = parseInt(scaleSlider.value) / 100;
        renderCanvas();
      }
    });

    controls.appendChild(layerUpBtn);
    controls.appendChild(layerDnBtn);
    controls.appendChild(removeBtn);
    controls.appendChild(clearBtn);
    controls.appendChild(scaleLabel);
    controls.appendChild(scaleSlider);
    panel.appendChild(controls);

    // --- Summary ---
    var summary = el('div', 'studio-summary');
    var itemCount = el('span', 'item-count', '0 items on canvas');
    var totalPrice = el('span', 'total-price', '\u20AC0.00');
    summary.appendChild(itemCount);
    summary.appendChild(totalPrice);
    panel.appendChild(summary);

    // --- Upsell Section ---
    var upsell = el('div', 'studio-upsell');
    var upsellTitle = el('h5', '', '');
    upsellTitle.appendChild(document.createTextNode('\u2728 Complete the Look'));
    upsell.appendChild(upsellTitle);
    var upsellGrid = el('div', 'upsell-items-grid');
    upsell.appendChild(upsellGrid);
    var bundleBtn = btn('upsell-bundle-cta', '\uD83D\uDED2 Add Bundle (15% off)', addBundleToCart);
    upsell.appendChild(bundleBtn);
    panel.appendChild(upsell);

    // --- Challenge Banner ---
    var challenge = el('div', 'studio-challenge-banner');
    var challengeText = el('span', '', '');
    var boldText = el('strong', '', '\uD83C\uDFC6 Style Challenge: ');
    challengeText.appendChild(boldText);
    challengeText.appendChild(document.createTextNode('Win \u20AC50! Share your look'));
    challenge.appendChild(challengeText);
    var challengeBtn = btn('challenge-enter-btn', 'Enter Challenge', enterChallenge);
    challenge.appendChild(challengeBtn);
    panel.appendChild(challenge);

    // --- CTAs ---
    var ctas = el('div', 'studio-ctas');
    var addCartBtn = btn('studio-cta-btn studio-cta-primary', '\uD83D\uDED2 Add All to Cart', addAllToCart);
    var saveLookBtn = btn('studio-cta-btn studio-cta-secondary', '\uD83D\uDCBE Save This Look', saveLook);
    var guideBtn = btn('studio-cta-btn studio-cta-accent', '\uD83D\uDCDA Get Capsule Guide (\u20AC9.99)', getCapsuleGuide);
    ctas.appendChild(addCartBtn);
    ctas.appendChild(saveLookBtn);
    ctas.appendChild(guideBtn);
    panel.appendChild(ctas);

    // Store DOM refs
    dom.panel = panel;
    dom.canvas = canvas;
    dom.ctx = canvas.getContext('2d');
    dom.canvasWrap = canvasWrap;
    dom.emptyState = emptyState;
    dom.dropOverlay = dropOverlay;
    dom.lookImageWrap = lookImageWrap;
    dom.lookImage = lookImage;
    dom.lookSpinner = lookSpinner;
    dom.lookSpinnerMsg = lookSpinnerMsg;
    dom.itemsBar = itemsBar;
    dom.controls = controls;
    dom.scaleSlider = scaleSlider;
    dom.summary = summary;
    dom.itemCount = itemCount;
    dom.totalPrice = totalPrice;
    dom.upsell = upsell;
    dom.upsellGrid = upsellGrid;
    dom.challenge = challenge;
    dom.ctas = ctas;

    return panel;
  }

  // ============================================================
  // INJECT INTO CHAT
  // ============================================================
  function inject() {
    var panel = buildDOM();

    // Try to inject into the Lucy chatbot container
    var chatContainer = document.getElementById('wowstoreAiChatbot') ||
                        document.querySelector('.wowstore-ai-chatbot') ||
                        document.querySelector('.ai-chatbot-container');

    if (chatContainer) {
      // Insert before the input area
      var inputArea = chatContainer.querySelector('.ai-chat-input-container') ||
                      chatContainer.querySelector('.chat-input-area') ||
                      chatContainer.querySelector('form');
      if (inputArea && inputArea.parentElement) {
        inputArea.parentElement.insertBefore(panel, inputArea);
      } else {
        chatContainer.appendChild(panel);
      }
    } else {
      // Fallback: append to body as floating panel
      panel.style.position = 'fixed';
      panel.style.bottom = '80px';
      panel.style.right = '16px';
      panel.style.width = '340px';
      panel.style.maxHeight = '85vh';
      panel.style.overflowY = 'auto';
      panel.style.zIndex = '10001';
      panel.style.borderRadius = '12px';
      panel.style.boxShadow = '0 8px 32px rgba(0,0,0,0.2)';
      document.body.appendChild(panel);
    }

    setupCanvasEvents();
    console.log('[LucyStudio] Injected into chat');
  }

  // ============================================================
  // CANVAS EVENTS (mouse + touch)
  // ============================================================
  function setupCanvasEvents() {
    var c = dom.canvas;

    c.addEventListener('mousedown', function(e) {
      var pos = getCanvasPos(e);
      handlePointerDown(pos);
    });
    c.addEventListener('mousemove', function(e) {
      var pos = getCanvasPos(e);
      handlePointerMove(pos);
    });
    c.addEventListener('mouseup', handlePointerUp);
    c.addEventListener('mouseleave', handlePointerUp);

    c.addEventListener('touchstart', function(e) {
      e.preventDefault();
      var pos = getTouchPos(e);
      handlePointerDown(pos);
    }, { passive: false });
    c.addEventListener('touchmove', function(e) {
      e.preventDefault();
      var pos = getTouchPos(e);
      handlePointerMove(pos);
    }, { passive: false });
    c.addEventListener('touchend', handlePointerUp);
  }

  function getCanvasPos(e) {
    var r = dom.canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (CFG.CANVAS_W / r.width),
      y: (e.clientY - r.top) * (CFG.CANVAS_H / r.height)
    };
  }

  function getTouchPos(e) {
    var t = e.touches[0] || e.changedTouches[0];
    var r = dom.canvas.getBoundingClientRect();
    return {
      x: (t.clientX - r.left) * (CFG.CANVAS_W / r.width),
      y: (t.clientY - r.top) * (CFG.CANVAS_H / r.height)
    };
  }

  function handlePointerDown(pos) {
    var item = getItemAtPos(pos);
    if (item) {
      state.selectedItem = item;
      state.isDragging = true;
      state.dragOffset = { x: pos.x - item.x, y: pos.y - item.y };
      dom.canvas.style.cursor = 'grabbing';
      // Update scale slider
      dom.scaleSlider.value = Math.round((item.scale || 1) * 100);
    } else {
      state.selectedItem = null;
    }
    renderCanvas();
    updateItemsBar();
  }

  function handlePointerMove(pos) {
    if (state.isDragging && state.selectedItem) {
      state.selectedItem.x = pos.x - state.dragOffset.x;
      state.selectedItem.y = pos.y - state.dragOffset.y;
      renderCanvas();
    } else {
      // Hover cursor
      var item = getItemAtPos(pos);
      dom.canvas.style.cursor = item ? 'grab' : 'default';
    }
  }

  function handlePointerUp() {
    state.isDragging = false;
    dom.canvas.style.cursor = 'default';
  }

  function getItemAtPos(pos) {
    // Check items from top layer down
    var sorted = state.canvasItems.slice().sort(function(a, b) {
      return (b.layer || 0) - (a.layer || 0);
    });
    for (var i = 0; i < sorted.length; i++) {
      var it = sorted[i];
      var s = it.scale || 1;
      var w = (it.imgW || 100) * s;
      var h = (it.imgH || 140) * s;
      if (pos.x >= it.x - w/2 && pos.x <= it.x + w/2 &&
          pos.y >= it.y - h/2 && pos.y <= it.y + h/2) {
        return it;
      }
    }
    return null;
  }

  // ============================================================
  // CANVAS RENDERING
  // ============================================================
  function renderCanvas() {
    var ctx = dom.ctx;
    ctx.clearRect(0, 0, CFG.CANVAS_W, CFG.CANVAS_H);

    // Background
    ctx.fillStyle = '#f8f8f8';
    ctx.fillRect(0, 0, CFG.CANVAS_W, CFG.CANVAS_H);

    // Body silhouette guide (subtle)
    drawBodyGuide(ctx);

    // Show/hide empty state
    dom.emptyState.style.display = state.canvasItems.length === 0 ? 'flex' : 'none';

    // Sort by layer order
    var sorted = state.canvasItems.slice().sort(function(a, b) {
      return (a.layer || 0) - (b.layer || 0);
    });

    // Draw each item
    sorted.forEach(function(item) {
      var img = state.images[item.productId];
      if (!img || !img.complete) return;

      var s = item.scale || 1;
      var w = (item.imgW || 100) * s;
      var h = (item.imgH || 140) * s;
      var dx = item.x - w/2;
      var dy = item.y - h/2;

      ctx.save();
      ctx.globalAlpha = item === state.selectedItem ? 0.9 : 1.0;
      ctx.drawImage(img, dx, dy, w, h);

      // Selection border
      if (item === state.selectedItem) {
        ctx.strokeStyle = '#e91e8c';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(dx - 2, dy - 2, w + 4, h + 4);
        ctx.setLineDash([]);
      }
      ctx.restore();
    });
  }

  function drawBodyGuide(ctx) {
    ctx.save();
    ctx.strokeStyle = 'rgba(0,0,0,0.06)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);

    // Head
    ctx.beginPath();
    ctx.ellipse(150, 35, 18, 22, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Shoulders
    ctx.beginPath();
    ctx.moveTo(100, 70);
    ctx.lineTo(200, 70);
    ctx.stroke();

    // Torso
    ctx.beginPath();
    ctx.moveTo(110, 70);
    ctx.lineTo(105, 200);
    ctx.moveTo(190, 70);
    ctx.lineTo(195, 200);
    ctx.stroke();

    // Waist
    ctx.beginPath();
    ctx.moveTo(105, 200);
    ctx.lineTo(195, 200);
    ctx.stroke();

    // Legs
    ctx.beginPath();
    ctx.moveTo(115, 200);
    ctx.lineTo(110, 340);
    ctx.moveTo(185, 200);
    ctx.lineTo(190, 340);
    ctx.stroke();

    // Feet
    ctx.beginPath();
    ctx.moveTo(95, 340);
    ctx.lineTo(125, 340);
    ctx.moveTo(175, 340);
    ctx.lineTo(205, 340);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.restore();
  }

  // ============================================================
  // ITEM MANAGEMENT
  // ============================================================
  function addToCanvas(product) {
    if (!product || !product.id) return;

    // Check if already on canvas
    var exists = state.canvasItems.some(function(it) {
      return it.productId === product.id || it.productId === String(product.id);
    });
    if (exists) {
      showToast('Already on canvas!');
      return;
    }

    var cat = guessCategory(product.title);
    var layer = CFG.LAYER_ORDER[cat] || 4;
    var pos = CFG.BODY_POSITIONS[cat] || { x: 150, y: 200 };

    var canvasItem = {
      productId: String(product.id),
      variantId: product.variantId || product.variants && product.variants[0] && product.variants[0].id,
      // Magic Mirror v2 (Task 1.2): keep handle so we can resolve a real
      // VARIANT id at add-to-cart time when variantId is missing.
      handle: product.handle || (product.url ? String(product.url).split('?')[0].replace(/^.*\/products\//, '') : ''),
      title: product.title,
      price: product.price,
      image: product.image || product.featured_image,
      category: cat,
      layer: layer,
      x: pos.x,
      y: pos.y,
      scale: 0.8,
      imgW: 100,
      imgH: 140
    };

    // Load image
    loadItemImage(canvasItem);

    state.canvasItems.push(canvasItem);

    // Also add to items list if not there
    var inList = state.items.some(function(it) { return String(it.id) === String(product.id); });
    if (!inList) {
      state.items.push(product);
    }

    updateUI();
    showToast(product.title + ' added to studio!');

    // Auto-open if not open
    if (!state.isOpen) openStudio();

    // Trigger upsell after 2+ items
    if (state.canvasItems.length >= 2 && !state.upsellShown) {
      setTimeout(fetchUpsellRecommendations, 800);
    }
  }

  function loadItemImage(canvasItem) {
    if (state.images[canvasItem.productId]) return;

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function() {
      // Scale to fit within 100x140 bounding box
      var aspect = img.naturalWidth / img.naturalHeight;
      if (aspect > 100/140) {
        canvasItem.imgW = 100;
        canvasItem.imgH = 100 / aspect;
      } else {
        canvasItem.imgH = 140;
        canvasItem.imgW = 140 * aspect;
      }
      state.images[canvasItem.productId] = img;
      renderCanvas();
    };
    img.onerror = function() {
      console.warn('[LucyStudio] Failed to load image for', canvasItem.title);
    };
    img.src = canvasItem.image;
  }

  function removeFromCanvas(productId) {
    state.canvasItems = state.canvasItems.filter(function(it) {
      return it.productId !== String(productId);
    });
    if (state.selectedItem && state.selectedItem.productId === String(productId)) {
      state.selectedItem = null;
    }
    updateUI();
  }

  function removeSelected() {
    if (!state.selectedItem) {
      showToast('Select an item first');
      return;
    }
    removeFromCanvas(state.selectedItem.productId);
    showToast('Item removed');
  }

  function clearCanvas() {
    state.canvasItems = [];
    state.selectedItem = null;
    updateUI();
    showToast('Canvas cleared');
  }

  function moveLayer(dir) {
    if (!state.selectedItem) {
      showToast('Select an item first');
      return;
    }
    state.selectedItem.layer = (state.selectedItem.layer || 0) + dir;
    renderCanvas();
  }

  // ============================================================
  // UI UPDATES
  // ============================================================
  function updateUI() {
    renderCanvas();
    updateItemsBar();
    updateSummary();
    updateChallenge();
  }

  function updateItemsBar() {
    var bar = dom.itemsBar;
    // Clear existing
    while (bar.firstChild) bar.removeChild(bar.firstChild);

    state.items.forEach(function(product) {
      var onCanvas = state.canvasItems.some(function(ci) {
        return ci.productId === String(product.id);
      });
      var isSelected = state.selectedItem && state.selectedItem.productId === String(product.id);

      var thumb = el('div', 'studio-item-thumb' + (onCanvas ? ' on-canvas' : '') + (isSelected ? ' selected' : ''));

      var img = document.createElement('img');
      img.src = product.image || product.featured_image || '';
      img.alt = product.title || '';
      img.loading = 'lazy';
      thumb.appendChild(img);

      // Remove badge
      var badge = el('span', 'remove-badge', '\u2715');
      badge.addEventListener('click', function(e) {
        e.stopPropagation();
        removeFromCanvas(product.id);
      });
      thumb.appendChild(badge);

      // MM v2 Task 2.4: swap badge — tap to see in-stock alternates for this piece.
      var ci = state.canvasItems.find(function(c) { return c.productId === String(product.id); });
      if (ci) {
        var swapBadge = el('span', 'swap-badge', '\u21BB'); // clockwise open circle arrow
        swapBadge.title = 'Swap this piece';
        swapBadge.addEventListener('click', function(e) {
          e.stopPropagation();
          openAlternatesPicker(ci);
        });
        thumb.appendChild(swapBadge);
      }

      // Click to toggle on/off canvas
      thumb.addEventListener('click', function() {
        if (onCanvas) {
          // Select it on canvas
          state.selectedItem = state.canvasItems.find(function(ci) {
            return ci.productId === String(product.id);
          });
          dom.scaleSlider.value = Math.round((state.selectedItem.scale || 1) * 100);
          renderCanvas();
          updateItemsBar();
        } else {
          addToCanvas(product);
        }
      });

      bar.appendChild(thumb);
    });
  }

  function updateSummary() {
    var count = state.canvasItems.length;
    dom.itemCount.textContent = count + ' item' + (count !== 1 ? 's' : '') + ' on canvas';

    var total = 0;
    state.canvasItems.forEach(function(it) {
      var p = parseFloat(it.price);
      if (!isNaN(p)) total += p;
    });
    dom.totalPrice.textContent = '\u20AC' + total.toFixed(2);
  }

  function updateChallenge() {
    // Show challenge banner when 3+ items
    if (state.canvasItems.length >= 3) {
      dom.challenge.classList.add('active');
    } else {
      dom.challenge.classList.remove('active');
    }
  }

  // ============================================================
  // UPSELL ENGINE
  // ============================================================
  function fetchUpsellRecommendations() {
    state.upsellShown = true;

    // Determine missing categories
    var onCanvasCats = {};
    state.canvasItems.forEach(function(it) { onCanvasCats[it.category] = true; });

    var needed = [];
    if (!onCanvasCats.footwear) needed.push('shoes boots');
    if (!onCanvasCats.accessories) needed.push('accessories jewelry');
    if (!onCanvasCats.outerwear && !onCanvasCats.tops) needed.push('jacket blazer');
    if (!onCanvasCats.bottoms && !onCanvasCats.dresses) needed.push('pants skirt');

    if (needed.length === 0) {
      needed.push('accessories jewelry scarf');
    }

    // Search Shopify for complementary items
    var query = needed.slice(0, 2).join(' ');
    fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (products.length === 0) return;

        // Filter out items already on canvas
        products = products.filter(function(p) {
          return !state.canvasItems.some(function(ci) { return ci.productId === String(p.id); });
        });

        renderUpsellItems(products.slice(0, 4));
      })
      .catch(function(err) {
        console.warn('[LucyStudio] Upsell fetch error:', err);
      });
  }

  function renderUpsellItems(products) {
    var grid = dom.upsellGrid;
    while (grid.firstChild) grid.removeChild(grid.firstChild);

    products.forEach(function(p) {
      var item = el('div', 'upsell-item');

      var img = document.createElement('img');
      img.src = p.image || (p.featured_image && p.featured_image.url) || '';
      img.alt = p.title || '';
      img.loading = 'lazy';
      item.appendChild(img);

      item.appendChild(el('div', 'upsell-title', p.title || ''));
      item.appendChild(el('div', 'upsell-price', formatPrice(p.price)));

      item.addEventListener('click', function() {
        addToCanvas({
          id: p.id,
          title: p.title,
          price: p.price,
          image: p.image || (p.featured_image && p.featured_image.url) || '',
          variantId: p.variants && p.variants[0] && p.variants[0].id,
          url: p.url
        });
      });

      grid.appendChild(item);
    });

    // Show upsell section
    dom.upsell.classList.add('active');
  }

  // ============================================================
  // MM v2 Task 2.4: TAP-TO-SWAP (Magic Mirror)
  // ============================================================
  // Stable browser id — mirror the canonical Lucy source so events line up
  // with the rest of the funnel; fall back to the same localStorage scheme.
  function getBrowserId() {
    try {
      if (window.LucyQuota && typeof window.LucyQuota.getBrowserId === 'function') {
        var q = window.LucyQuota.getBrowserId();
        if (q) return q;
      }
    } catch (e) {}
    try {
      var id = localStorage.getItem('lucy_browser_id');
      if (!id) {
        id = 'lb_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
        localStorage.setItem('lucy_browser_id', id);
      }
      return id;
    } catch (e) {
      return 'lb_anon_' + Math.random().toString(36).substring(2, 8);
    }
  }

  // Stable look id for this look-editing session.
  function getLookId() {
    if (state.lookId) return state.lookId;
    var id;
    try { id = localStorage.getItem('lucy_studio_look_id'); } catch (e) {}
    if (!id) {
      id = 'look_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
      try { localStorage.setItem('lucy_studio_look_id', id); } catch (e) {}
    }
    state.lookId = id;
    return id;
  }

  // The hosted URL we hand to /look-edit as the "current look".
  // Prefer a previously generated look image; otherwise seed from the piece's
  // own hosted product image (a real URL the API can fetch). Never a data: URL.
  function currentLookImageUrl(piece) {
    if (state.lookImageUrl) return state.lookImageUrl;
    try {
      if (window.LucyTryOn && typeof window.LucyTryOn.getResultImage === 'function') {
        var r = window.LucyTryOn.getResultImage();
        if (r && /^https?:/.test(r)) return r;
      }
    } catch (e) {}
    if (piece && piece.image && /^https?:/.test(piece.image)) return piece.image;
    return null;
  }

  // Read alternates from either response shape: a bare array, or { alternates: [] }.
  function parseAlternates(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.alternates)) return data.alternates;
    return [];
  }

  // Map a category to the backend's expected coarse category param.
  function swapCategoryParam(piece) {
    return (piece && piece.category) ? piece.category : '';
  }

  function closeAlternatesPicker() {
    state.swapPiece = null;
    var existing = dom.panel && dom.panel.querySelector('.studio-swap-picker');
    if (existing) existing.remove();
  }

  // Tap a look piece -> fetch 2-3 in-stock alternates -> show inline picker.
  function openAlternatesPicker(piece) {
    if (!piece) return;
    if (state.swapBusy) { showToast('Hold on — still updating your look'); return; }
    closeAlternatesPicker();
    state.swapPiece = piece;

    var picker = el('div', 'studio-swap-picker');
    var head = el('div', 'studio-swap-head');
    head.appendChild(el('span', 'studio-swap-title', 'Swap: ' + (piece.title || 'this piece')));
    var x = btn('studio-swap-close', '✕', closeAlternatesPicker);
    head.appendChild(x);
    picker.appendChild(head);

    var body = el('div', 'studio-swap-body');
    body.appendChild(el('div', 'studio-swap-loading', 'Finding alternates…'));
    picker.appendChild(body);

    // Anchor the picker right under the items bar.
    if (dom.itemsBar && dom.itemsBar.parentElement) {
      dom.itemsBar.parentElement.insertBefore(picker, dom.itemsBar.nextSibling);
    } else {
      dom.panel.appendChild(picker);
    }

    var handle = piece.handle || '';
    if (!handle) { body.textContent = 'No alternates available for this piece.'; return; }

    var url = CFG.AI_API_BASE + '/api/lucy/alternates?handle=' + encodeURIComponent(handle) +
              '&category=' + encodeURIComponent(swapCategoryParam(piece)) + '&limit=3';
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (state.swapPiece !== piece) return; // picker changed/closed mid-flight
        var alts = parseAlternates(data).filter(function(a) {
          return a && (a.variant_id || a.product_id);
        });
        while (body.firstChild) body.removeChild(body.firstChild);
        if (alts.length === 0) {
          body.appendChild(el('div', 'studio-swap-empty', 'No in-stock alternates right now.'));
          return;
        }
        alts.slice(0, 3).forEach(function(a) {
          var card = el('div', 'studio-swap-alt');
          var img = document.createElement('img');
          img.src = a.image || '';
          img.alt = a.title || '';
          img.loading = 'lazy';
          card.appendChild(img);
          card.appendChild(el('div', 'studio-swap-alt-title', a.title || ''));
          card.appendChild(el('div', 'studio-swap-alt-price', formatPrice(a.price)));
          card.addEventListener('click', function() { pickAlternate(piece, a); });
          body.appendChild(card);
        });
      })
      .catch(function(err) {
        console.warn('[LucyStudio] alternates fetch error:', err);
        if (state.swapPiece !== piece) return;
        while (body.firstChild) body.removeChild(body.firstChild);
        body.appendChild(el('div', 'studio-swap-empty', "Couldn't load alternates, try again"));
      });
  }

  function setSwapLoading(on, msg) {
    state.swapBusy = on;
    if (dom.lookImageWrap) dom.lookImageWrap.classList.toggle('loading', on);
    if (dom.lookSpinner) dom.lookSpinner.style.display = on ? 'flex' : 'none';
    if (msg && dom.lookSpinnerMsg) dom.lookSpinnerMsg.textContent = msg;
    // Disable the open picker while generating.
    var picker = dom.panel && dom.panel.querySelector('.studio-swap-picker');
    if (picker) picker.classList.toggle('disabled', on);
  }

  function showLookImage(imageUrl) {
    if (!dom.lookImage || !dom.lookImageWrap) return;
    state.lookImageUrl = imageUrl;
    dom.lookImage.src = imageUrl;
    dom.lookImageWrap.classList.add('active');
  }

  // Pick an alternate -> call /look-edit (~15s) -> on success swap the piece in
  // state (so Add-all uses the new variant) and replace the displayed look image.
  function pickAlternate(piece, alt) {
    if (state.swapBusy) return;
    var lookUrl = currentLookImageUrl(piece);
    if (!lookUrl) {
      showToast("Can't edit the look yet — open it in the Magic Mirror first");
      return;
    }

    // Snapshot the piece for restore-on-error.
    var prior = {
      productId: piece.productId, variantId: piece.variantId, handle: piece.handle,
      title: piece.title, price: piece.price, image: piece.image
    };
    var priorLookUrl = state.lookImageUrl;

    setSwapLoading(true, 'Updating your look…');

    var payload = {
      look_image_url: lookUrl,
      garment_label: swapCategoryParam(piece) || (piece.title || ''),
      new_product_image_url: alt.image,
      browser_id: getBrowserId(),
      look_id: getLookId()
    };

    fetch(CFG.AI_API_BASE + '/api/lucy/look-edit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setSwapLoading(false);
        if (data && data.capped) {
          showToast(data.message || 'Daily look edits reached — try again tomorrow');
          return;
        }
        if (!data || !data.image_url) {
          showToast("Couldn't update the look, try again");
          return;
        }

        // 1) Replace the displayed look image.
        showLookImage(data.image_url);

        // 2) Swap the piece in look/canvas state so Add-all adds the NEW variant.
        piece.productId = alt.product_id != null ? String(alt.product_id) : piece.productId;
        piece.variantId = alt.variant_id != null ? alt.variant_id : piece.variantId;
        piece.handle = alt.handle || piece.handle;
        piece.title = alt.title || piece.title;
        piece.price = (alt.price != null) ? alt.price : piece.price;
        piece.image = alt.image || piece.image;

        // Keep the items list (thumbnails) in sync with the swapped piece.
        for (var i = 0; i < state.items.length; i++) {
          if (String(state.items[i].id) === prior.productId) {
            state.items[i] = {
              id: piece.productId, title: piece.title, price: piece.price,
              image: piece.image, variantId: piece.variantId, handle: piece.handle,
              url: piece.handle ? ('/products/' + piece.handle) : state.items[i].url
            };
            break;
          }
        }

        // Refresh the canvas thumbnail for the new product image.
        delete state.images[prior.productId];
        loadItemImage(piece);

        // 3) Refresh UI + total price; close the picker.
        closeAlternatesPicker();
        updateUI();
        showToast('Swapped to ' + (piece.title || 'new piece'));
      })
      .catch(function(err) {
        console.warn('[LucyStudio] look-edit error:', err);
        setSwapLoading(false);
        // Restore prior state on failure.
        piece.productId = prior.productId; piece.variantId = prior.variantId;
        piece.handle = prior.handle; piece.title = prior.title;
        piece.price = prior.price; piece.image = prior.image;
        if (priorLookUrl) { state.lookImageUrl = priorLookUrl; dom.lookImage.src = priorLookUrl; }
        showToast("Couldn't update the look, try again");
      });
  }

  // ============================================================
  // CART + ACTIONS
  // ============================================================
  // Magic Mirror v2 (Task 1.2): cache resolved variant ids by handle.
  var _variantCache = {};

  // Resolve a real, available VARIANT id for a canvas piece.
  // /cart/add.js needs a VARIANT id; the old code fell back to PRODUCT id,
  // which made the whole add fail. Returns null when unresolvable.
  function resolveVariantId(piece) {
    return new Promise(function(resolve) {
      var pid = piece.productId ? String(piece.productId) : '';
      var vid = piece.variantId != null ? String(piece.variantId) : '';
      // A usable variant id is numeric, non-blank, and not just the product id.
      if (vid && vid !== pid && /^[0-9]+$/.test(vid)) {
        resolve(parseInt(vid, 10));
        return;
      }
      // Derive a handle to look the product up.
      var handle = piece.handle || '';
      if (!handle && piece.url) {
        handle = String(piece.url).split('?')[0].replace(/^.*\/products\//, '');
      }
      if (!handle) { resolve(null); return; }
      if (_variantCache[handle] !== undefined) { resolve(_variantCache[handle]); return; }
      fetch('/products/' + handle + '.js')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var vs = (data && data.variants) || [];
          var v = vs.filter(function(x) { return x && x.available; })[0] || vs[0];
          var id = v && v.id ? parseInt(v.id, 10) : null;
          _variantCache[handle] = id;
          resolve(id);
        })
        .catch(function() { _variantCache[handle] = null; resolve(null); });
    });
  }

  function addAllToCart() {
    if (state.canvasItems.length === 0) {
      showToast('Add items to canvas first');
      return;
    }

    var pieces = state.canvasItems.slice();
    Promise.all(pieces.map(function(p) {
      return resolveVariantId(p).then(function(id) { return { id: id, piece: p }; });
    })).then(function(resolved) {
      var items = [];
      var unavailable = 0;
      resolved.forEach(function(r) {
        if (r.id && !isNaN(r.id)) {
          // Preserve any line-item properties already on the piece.
          var line = { id: r.id, quantity: 1 };
          if (r.piece.properties) line.properties = r.piece.properties;
          items.push(line);
        } else {
          unavailable++;
        }
      });

      if (items.length === 0) {
        showToast('Nothing available to add');
        return;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var msg = items.length + ' added to cart';
        if (unavailable > 0) msg += ' \u00b7 ' + unavailable + ' unavailable';
        showToast(msg);
        // Update cart count in header if present
        var cartCount = document.querySelector('.cart-count, [data-cart-count]');
        if (cartCount) {
          var current = parseInt(cartCount.textContent) || 0;
          cartCount.textContent = current + items.length;
        }
      })
      .catch(function(err) {
        console.error('[LucyStudio] Cart error:', err);
        showToast('Error adding to cart');
      });
    });
  }

  function addBundleToCart() {
    // Add all canvas items + all upsell items with 15% discount note
    showToast('Bundle added! 15% off applied at checkout');
    addAllToCart();
    // In production, apply discount code automatically
    fetch('/discount/BUNDLE15', { method: 'GET' }).catch(function() {});
  }

  function saveLook() {
    if (state.canvasItems.length === 0) {
      showToast('Add items first');
      return;
    }

    var lookData = {
      items: state.canvasItems.map(function(it) {
        return {
          productId: it.productId,
          title: it.title,
          price: it.price,
          image: it.image,
          category: it.category,
          x: it.x,
          y: it.y,
          scale: it.scale,
          layer: it.layer
        };
      }),
      timestamp: Date.now()
    };

    // Try API first, then localStorage fallback
    fetch(CFG.API_BASE + '/api/looks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: 'Lucy Studio Look ' + new Date().toLocaleDateString(),
        items: lookData.items
      })
    })
    .then(function(r) {
      if (!r.ok) throw new Error('API unavailable');
      return r.json();
    })
    .then(function(data) {
      showToast('Look saved! Share code: ' + (data.shareCode || 'saved'));
    })
    .catch(function() {
      // localStorage fallback
      var looks = JSON.parse(localStorage.getItem('lucy_saved_looks') || '[]');
      looks.push(lookData);
      localStorage.setItem('lucy_saved_looks', JSON.stringify(looks));
      showToast('Look saved locally!');
    });
  }

  function getCapsuleGuide() {
    if (state.canvasItems.length === 0) {
      showToast('Add items to get a personalized guide');
      return;
    }

    var items = state.canvasItems.map(function(it) {
      return it.title + ' (' + it.category + ')';
    }).join(', ');

    showToast('Generating your capsule guide...');

    // Call n8n webhook for AI capsule guide generation
    fetch('https://n8n.wowstore.live/webhook/capsule-guide', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        canvasItems: state.canvasItems.map(function(it) {
          return { title: it.title, category: it.category, price: it.price };
        })
      })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.guideUrl) {
        window.open(data.guideUrl, '_blank');
        showToast('Capsule guide ready!');
      } else {
        showToast('Guide will be emailed to you!');
      }
    })
    .catch(function() {
      showToast('Guide coming soon!');
    });
  }

  function enterChallenge() {
    if (state.canvasItems.length < 3) {
      showToast('Add at least 3 items to enter');
      return;
    }

    // Capture canvas as image
    var dataUrl = dom.canvas.toDataURL('image/png');

    fetch(CFG.API_BASE + '/api/challenges/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lookImage: dataUrl,
        items: state.canvasItems.map(function(it) {
          return { productId: it.productId, title: it.title };
        })
      })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      showToast('Entry submitted! Good luck!');
    })
    .catch(function() {
      showToast('Challenge entry saved!');
    });
  }

  // ============================================================
  // OPEN / CLOSE
  // ============================================================
  function openStudio() {
    if (!dom.panel) inject();
    dom.panel.classList.add('active');
    state.isOpen = true;
    renderCanvas();
    console.log('[LucyStudio] Opened');
  }

  function closeStudio() {
    if (dom.panel) dom.panel.classList.remove('active');
    state.isOpen = false;
    console.log('[LucyStudio] Closed');
  }

  // ============================================================
  // TOAST NOTIFICATION STYLE (injected once)
  // ============================================================
  function injectToastStyle() {
    var style = document.createElement('style');
    style.textContent = [
      '.lucy-studio-toast {',
      '  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%) translateY(20px);',
      '  background: #333; color: #fff; padding: 8px 16px; border-radius: 8px;',
      '  font-size: 13px; z-index: 99999; opacity: 0; transition: all 0.3s;',
      '  pointer-events: none; white-space: nowrap;',
      '}',
      '.lucy-studio-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }',
      /* MM v2 Task 2.4: tap-to-swap UI */
      '.studio-canvas-wrap { position: relative; }',
      '.studio-look-image-wrap { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; background: #f8f8f8; z-index: 3; }',
      '.studio-look-image-wrap.active { display: flex; }',
      '.studio-look-image { max-width: 100%; max-height: 100%; object-fit: contain; }',
      '.studio-look-image-wrap.loading .studio-look-image { filter: brightness(0.7); }',
      '.studio-look-spinner { display: none; position: absolute; inset: 0; flex-direction: column; align-items: center; justify-content: center; gap: 10px; background: rgba(255,255,255,0.55); z-index: 4; }',
      '.studio-look-spinner-ring { width: 34px; height: 34px; border: 3px solid #e7e7e7; border-top-color: #e91e8c; border-radius: 50%; animation: lucySpin 0.9s linear infinite; }',
      '.studio-look-spinner-msg { font-size: 12px; color: #444; }',
      '@keyframes lucySpin { to { transform: rotate(360deg); } }',
      '.studio-item-thumb { position: relative; }',
      '.swap-badge { position: absolute; bottom: -6px; right: -6px; width: 18px; height: 18px; line-height: 18px; text-align: center; font-size: 12px; background: #e91e8c; color: #fff; border-radius: 50%; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.3); z-index: 2; }',
      '.studio-swap-picker { background: #fff; border: 1px solid #eee; border-radius: 10px; margin: 6px 8px; padding: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.08); }',
      '.studio-swap-picker.disabled { opacity: 0.5; pointer-events: none; }',
      '.studio-swap-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }',
      '.studio-swap-title { font-size: 12px; font-weight: 600; color: #333; }',
      '.studio-swap-close { border: none; background: none; font-size: 14px; cursor: pointer; color: #888; }',
      '.studio-swap-body { display: flex; gap: 8px; overflow-x: auto; }',
      '.studio-swap-loading, .studio-swap-empty { font-size: 12px; color: #888; padding: 6px 2px; }',
      '.studio-swap-alt { flex: 0 0 84px; width: 84px; cursor: pointer; border: 1px solid #f0f0f0; border-radius: 8px; padding: 4px; text-align: center; transition: border-color 0.15s; }',
      '.studio-swap-alt:hover { border-color: #e91e8c; }',
      '.studio-swap-alt img { width: 100%; height: 84px; object-fit: cover; border-radius: 6px; }',
      '.studio-swap-alt-title { font-size: 10px; color: #444; margin-top: 3px; line-height: 1.2; max-height: 24px; overflow: hidden; }',
      '.studio-swap-alt-price { font-size: 11px; font-weight: 600; color: #e91e8c; }'
    ].join('\n');
    document.head.appendChild(style);
  }

  // ============================================================
  // PUBLIC API
  // ============================================================
  window.LucyStudio = {
    open: openStudio,
    close: closeStudio,
    addToCanvas: function(product) {
      addToCanvas(product);
    },
    addProduct: function(product) {
      // Add to items list + canvas
      addToCanvas(product);
    },
    getCanvasItems: function() {
      return state.canvasItems.slice();
    },
    clear: clearCanvas,
    isOpen: function() { return state.isOpen; }
  };

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    injectToastStyle();

    // Defer injection until chat is available
    var attempts = 0;
    function tryInject() {
      var chatEl = document.getElementById('wowstoreAiChatbot') ||
                   document.querySelector('.ai-chatbot-container');
      if (chatEl || attempts > 20) {
        inject();
      } else {
        attempts++;
        setTimeout(tryInject, 500);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInject);
    } else {
      tryInject();
    }
  }

  init();
  console.log('[LucyStudio] Mini Studio v1.0 loaded');

})();
